import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ButtonHTMLAttributes,
  type CSSProperties,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';

import { cn } from '@/lib/cn';

/** Gap between trigger and panel, and the least breathing room at the viewport edge. */
const GAP = 8;

const HIDDEN: CSSProperties = { position: 'fixed', top: 0, left: 0, visibility: 'hidden' };

export interface DropdownProps {
  /** Contents of the trigger button. */
  trigger: ReactNode;
  /** Accessible name for the trigger, when its contents are icon-only. */
  label?: string;
  align?: 'left' | 'right';
  triggerClassName?: string;
  panelClassName?: string;
  /** Receives `close` so an item can dismiss the panel after acting. */
  children: (close: () => void) => ReactNode;
}

/**
 * Button plus a popover panel, dismissed by Escape or a click outside.
 *
 * Deliberately unopinionated about the panel's contents: the catalog uses it
 * for a row action menu, a sort list, a filter form and a category picker.
 *
 * The panel is portalled to the body and anchored to the trigger rather than
 * positioned inside it. Triggers sit in scroll containers - a table with
 * `overflow-x-auto`, a card with `overflow-hidden` - which would clip an
 * absolutely positioned panel no matter how high its z-index, and a short
 * table has no height to hide the clipping.
 */
export function Dropdown({
  trigger,
  label,
  align = 'left',
  triggerClassName,
  panelClassName,
  children,
}: DropdownProps) {
  const [open, setOpen] = useState(false);
  const [style, setStyle] = useState<CSSProperties>(HIDDEN);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panel = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;

    function onPointerDown(event: MouseEvent) {
      const target = event.target as Node;
      // The panel is no longer a descendant of the trigger, so it needs
      // checking on its own or every click inside it would dismiss.
      if (triggerRef.current?.contains(target) || panel.current?.contains(target)) return;
      setOpen(false);
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') setOpen(false);
    }

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  // Layout effect so the panel is placed before paint, never seen at 0,0.
  useLayoutEffect(() => {
    if (!open) {
      setStyle(HIDDEN);
      return;
    }

    function place() {
      const anchor = triggerRef.current?.getBoundingClientRect();
      const size = panel.current?.getBoundingClientRect();
      if (!anchor || !size) return;

      // Prefer below; flip above only when below overflows and above fits.
      const below = anchor.bottom + GAP;
      const above = anchor.top - GAP - size.height;
      const top = below + size.height > window.innerHeight - GAP && above > GAP ? above : below;

      const preferred = align === 'right' ? anchor.right - size.width : anchor.left;
      const rightMost = window.innerWidth - size.width - GAP;
      const left = Math.min(Math.max(GAP, preferred), Math.max(GAP, rightMost));

      setStyle({ position: 'fixed', top, left, visibility: 'visible' });
    }

    place();
    // Capture phase catches scrolls in the table and card, not just the window.
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [open, align]);

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={label}
        onClick={() => setOpen((current) => !current)}
        className={cn(
          'inline-flex items-center justify-center gap-2 rounded-xl text-sm font-medium',
          'text-ink transition-colors hover:bg-surface',
          triggerClassName,
        )}
      >
        {trigger}
      </button>

      {open
        ? createPortal(
            <div
              ref={panel}
              role="menu"
              style={style}
              className={cn(
                'z-50 min-w-48 rounded-xl border border-line bg-white p-1 shadow-lift',
                panelClassName,
              )}
            >
              {children(() => setOpen(false))}
            </div>,
            document.body,
          )
        : null}
    </>
  );
}

export interface DropdownItemProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  tone?: 'default' | 'danger';
  children: ReactNode;
}

export function DropdownItem({
  tone = 'default',
  className,
  children,
  ...props
}: DropdownItemProps) {
  return (
    <button
      type="button"
      role="menuitem"
      className={cn(
        'flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm',
        'transition-colors disabled:cursor-not-allowed disabled:opacity-60',
        tone === 'danger'
          ? 'text-red-600 hover:bg-red-50'
          : 'text-ink hover:bg-surface hover:text-brand-700',
        className,
      )}
      {...props}
    >
      {children}
    </button>
  );
}
